import React, { useRef, useState } from 'react';
import { Plus, Trash2, ImagePlus, Loader2, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { EditorShell, inputCls, labelCls } from './EditorShell';
import { useContent } from '../../context/ContentContext';
import { api, formatApiError, pictureUrl } from '../../lib/api';

const ImageField = ({ value, onChange }) => {
  const { uploadImage } = useContent();
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { onChange(await uploadImage(file)); toast.success('Biletet er lasta opp'); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="relative w-28 h-20 rounded-xl overflow-hidden bg-[#1a1424] border border-dashed border-white/20 shrink-0 flex items-center justify-center text-white/40 hover:border-[#ff2e2e] hover:text-[#ff2e2e]">
        {value ? <img src={pictureUrl(value)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
        <span className="relative bg-black/60 rounded-full p-2">{busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}</span>
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
      <input className={inputCls} placeholder="…eller lim inn bilete-URL" value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
};

const FileField = ({ value, onChange, accept = 'application/pdf' }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.path);
      toast.success('Fila er lasta opp');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl border border-dashed border-white/20 text-white/70 text-sm font-semibold hover:border-[#ff2e2e] hover:text-[#ff2e2e] shrink-0">{busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Last opp</button>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={upload} />
      <input className={inputCls} placeholder="…eller lim inn lenke" value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
};

const linesToItems = (txt) => txt.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
  const m = l.match(/^(\d{1,2}[:.]\d{2})\s*[-–—]?\s*(.*)$/);
  return m ? { time: m[1].replace('.', ':'), title: m[2] } : { time: '', title: l };
});
const R = {};
const itemsToLines = (items = []) => items.map((i) => (i.time ? `${i.time} - ${i.title}` : i.title)).join('\n');

export function Field({ f, value, onChange }) {
  if (f.type === 'image') return <ImageField value={value} onChange={onChange} />;
  if (f.type === 'file') return <FileField value={value} onChange={onChange} accept={f.accept} />;
  if (f.type === 'color') return <div className="flex items-center gap-3"><input type="color" aria-label={f.label} value={/^#[\da-f]{6}$/i.test(value || '') ? value : '#ff2e2e'} onChange={(e) => onChange(e.target.value)} className="h-12 w-14 rounded-lg border border-white/15 bg-transparent p-1" /><input className={inputCls} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="#ff2e2e" /></div>;
  if (f.type === 'select') return <select className={inputCls} value={value || f.options[0][0]} onChange={(e) => onChange(e.target.value)}>{f.options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>;
  if (f.type === 'multiselect') return <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">{f.options.map(([option, label]) => <label key={option} className="inline-flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={(value || []).includes(option)} onChange={(e) => onChange(e.target.checked ? [...(value || []), option] : (value || []).filter((item) => item !== option))} className="accent-[#ff2e2e] w-4 h-4 shrink-0" />{label}</label>)}</div>;
  if (f.type === 'textarea') return <textarea className={`${inputCls} resize-y`} rows={f.rows || 3} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} />;
  if (f.type === 'lines') return <textarea className={`${inputCls} resize-y font-mono text-sm`} rows={f.rows || 5} defaultValue={itemsToLines(value)} onBlur={(e) => onChange(linesToItems(e.target.value))} placeholder={'19:00 - Refuel night\n21:30 - ???'} />;
  if (f.type === 'checkbox') return <label className="inline-flex items-center gap-2 text-sm text-white/70 cursor-pointer"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-[#ff2e2e] w-4 h-4" /> {f.hint}</label>;
  if (f.type === 'list') return <R.List schema={f.schema} itemLabel={f.itemLabel} list={value || []} onChange={onChange} nested />;
  return <input className={inputCls} type={f.type === 'url' ? 'url' : 'text'} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} />;
}

export function ListFields({ schema, list, onChange, itemLabel = 'element', nested = false }) {
  const update = (i, k, v) => onChange(list.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= list.length) return; const n = [...list]; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  const blank = () => Object.fromEntries([['id', `i${Date.now()}`], ...schema.map((f) => [f.key, f.type === 'list' || f.type === 'lines' ? [] : f.type === 'checkbox' ? false : ''])]);
  return (
    <div className={`space-y-3 ${nested ? 'pl-3 border-l-2 border-white/10' : ''}`}>
      {list.map((it, i) => (
        <div key={it.id || i} className="rounded-2xl border border-white/10 p-4 space-y-3 bg-white/[0.02]" data-testid={`schema-item-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/40">{itemLabel} {i + 1}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} aria-label="Flytt opp" className="w-8 h-8 rounded-lg border border-white/10 text-white/50 hover:text-white flex items-center justify-center"><ChevronUp size={14} /></button>
              <button type="button" onClick={() => move(i, 1)} aria-label="Flytt ned" className="w-8 h-8 rounded-lg border border-white/10 text-white/50 hover:text-white flex items-center justify-center"><ChevronDown size={14} /></button>
              <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} aria-label="Fjern" data-testid={`schema-remove-${i}`} className="w-8 h-8 rounded-lg border border-white/10 text-white/50 hover:text-red-400 hover:border-red-500 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {schema.map((f) => (
              <div key={f.key} className={f.full || ['textarea', 'image', 'list', 'lines', 'file'].includes(f.type) ? 'sm:col-span-2' : ''}>
                <label className={labelCls}>{f.label}</label>
                <Field f={f} value={it[f.key]} onChange={(v) => update(i, f.key, v)} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, blank()])} data-testid="schema-add-button" className="w-full py-2.5 rounded-2xl border border-dashed border-white/20 text-white/60 font-semibold text-sm hover:border-[#ff2e2e] hover:text-[#ff2e2e] inline-flex items-center justify-center gap-2"><Plus size={14} /> Legg til {itemLabel}</button>
    </div>
  );
}

R.List = ListFields;

export const SchemaEditor = ({ contentKey, title, initial, schema, itemLabel, onClose, single = false }) => {
  const { saveContent } = useContent();
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await saveContent(contentKey, value); toast.success('Lagra!'); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <EditorShell title={title} onClose={onClose} onSave={save} saving={saving} testId={`${contentKey}-editor`}>
      {single ? (
        <div className="space-y-4">
          {schema.map((f) => (
            <div key={f.key}><label className={labelCls}>{f.label}</label><Field f={f} value={value?.[f.key]} onChange={(v) => setValue({ ...value, [f.key]: v })} />{f.help && <p className="text-white/35 text-xs mt-1">{f.help}</p>}</div>
          ))}
        </div>
      ) : (
        <ListFields schema={schema} list={value || []} onChange={setValue} itemLabel={itemLabel} />
      )}
    </EditorShell>
  );
};
